import { IDiscountCode, IStoreDiscount } from '../../interfaces/Store';
import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createAutomaticDiscountV2, createDiscountCodeV2, deleteAutomaticDiscountByIdV2, deleteDiscountCodeByIdV2, updateAutomaticDiscountByIdV2, updateDiscountCodeByIdV2, verifyDiscountCodeV2 } from '../../services/store.service';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';

export const useDeleteDiscountCodeById = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (discountId: string) => {
      try {
        const res = await deleteDiscountCodeByIdV2(storeId, discountId);
        if (!res) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res;
      } catch (e: any) {
        backendErrorsHandler(e);
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['discountCodes', { storeId: storeId }],
      })]);
    }
  });
};

export const useDeleteAutomaticDiscountById = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (discountId: string) => {
      try {
        const res = await deleteAutomaticDiscountByIdV2(storeId, discountId);
        if (!res) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['automaticDiscounts', { storeId: storeId }],
      })]);
    }
  });
};

export const useCreateDiscountCode = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IDiscountCode) => {
      try {
        const res = await createDiscountCodeV2(storeId, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['discountCodes', { storeId: storeId }],
      })]);
    }
  });
};

export const useCreateAutomaticDiscount = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IStoreDiscount) => {
      try {
        const res = await createAutomaticDiscountV2(storeId, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['automaticDiscounts', { storeId: storeId }],
      })]);
    }
  });
};


export const useUpdateAutomaticDiscount = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      discountId: string;
      data: {
        products: string[]
      }
    }) => {
      try {
        const res = await updateAutomaticDiscountByIdV2(storeId, data.discountId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['automaticDiscounts', { storeId: storeId }],
      })]);
    }
  });
};

export const useUpdateDiscountCode = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      discountId: string;
      data: {
        products?: string[];
        buyAndGetConditions?: any;
        freeProductSetting?: any;
        name?: string;
        code?: string;
        fromDate?: string;
        toDate?: string;
      }
    }) => {
      try {
        const res = await updateDiscountCodeByIdV2(storeId, data.discountId, data.data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['discountCodes', { storeId: storeId }],
      })]);
    }
  });
};

type VerifyDiscountCodeArgs = { code: string; orderSubtotal?: number };
export const useVerifyDiscountCode = (storeId: string) => useMutation<IDiscountCode, Error, VerifyDiscountCodeArgs | string>({
  mutationFn: async (input: VerifyDiscountCodeArgs | string) => {
    const args: VerifyDiscountCodeArgs = typeof input === 'string' ? { code: input } : input;
    try {
      const res = await verifyDiscountCodeV2(storeId, args.code, { orderSubtotal: args.orderSubtotal });
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while verifying the discount code.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});